import { Card, Grid, Typography } from '@mui/material'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import { CustomPagination } from 'components/purchase/CustomPagination'
import { NewsCard } from 'components/purchase/NewsCard'
import { NewsItem } from 'components/purchase/NewsItem'
import { ShareableCard } from 'components/purchase/ShareableCard'
import { PurchaseLayout } from 'hoc/PurchaseLayout'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useQueryState } from 'react-router-use-location-state'
import { useNewsItemQuery, useNewsQuery } from 'services/info-center/news'
import { IInfoCenterNewsItem } from 'types/info-center/news'

const breadCrumbs = [
  {
    id: 'home',
    title: 'Ana səhifə',
    path: '/',
  },
  {
    id: 'news',
    title: 'Xəbərlər',
  },
]

export type TBreadcrumb = { title: string; path?: string; id?: string }[]

export const News: React.FC = () => {
  const [breadCrumbItems, setBreadCrumbItems] = useState<TBreadcrumb>(breadCrumbs)
  const [moreNews, setMoreNews] = useState<IInfoCenterNewsItem[] | null>(null)
  let { id } = useParams()
  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10
  const { t } = useTranslation()

  const args = {
    page,
    size: rowsPerPage,
  } as const

  const { data, isError, isLoading } = useNewsQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  const { data: newsItem } = useNewsItemQuery({ id: id ?? '' }, { skip: !id })

  useEffect(() => {
    if (id && newsItem) {
      setBreadCrumbItems([
        {
          id: 'home',
          title: 'Ana səhifə',
          path: '/',
        },
        {
          id: 'news',
          title: 'Xəbərlər',
          path: '/news',
        },
        {
          title: newsItem.title,
        },
      ])
      const moreNewsItems = data?.items.filter((obj) => obj.id !== id).slice(0, 3)
      setMoreNews(moreNewsItems!)
    }
    return () => {
      setBreadCrumbItems(breadCrumbs)
      setMoreNews(null)
    }
  }, [id, newsItem])

  if (isLoading) {
    return <Loading fullPage={false} />
  }
  if (isError) {
    return <Error fullPage={false} />
  }
  if (data === undefined) {
    return <></>
  }

  const renderContent = () => {
    return (
      <>
        <Card>
          <Typography variant="h5">
            {id && newsItem ? newsItem.title : t('news')}
          </Typography>
          {id && newsItem ? (
            <NewsItem newsItem={newsItem} />
          ) : (
            <Grid container columnSpacing="24px">
              {data?.items.map((news: IInfoCenterNewsItem) => {
                return (
                  <Grid key={news.id} item lg={4} xs={12}>
                    <NewsCard news={news} />
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Card>

        {!id && data?.info.totalRows !== 0 && (
          <CustomPagination
            total={data?.info.totalRows!}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}

        {id && newsItem && (
          <>
            <ShareableCard />
            <Card sx={{ marginTop: '42px' }}>
              <Typography variant="h5">{t('other')}</Typography>
              <Grid container columnSpacing="24px" rowSpacing="24px">
                {moreNews?.map((news) => {
                  return (
                    <Grid key={news.id} item lg={4} xs={12}>
                      <NewsCard news={news} />
                    </Grid>
                  )
                })}
              </Grid>
            </Card>
          </>
        )}
      </>
    )
  }

  return (
    <PurchaseLayout breadCrumbItems={breadCrumbItems} content={renderContent()} />
  )
}
